<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class=" tw-pb-0">
        <div class="tw-flex tw-items-center" v-if="isUserLoading">
          <base-skeleton-loader class="tw-h-16 tw-w-16 md:tw-h-24 md:tw-w-24 tw-flex-shrink-0 tw-rounded-full" />
          <div class="tw-ml-4 tw-mb-px sm:tw-mb-0">
            <base-skeleton-loader class="tw-h-4 tw-w-48 tw-mt-2 sm:tw-mt-2.5 md:tw-mt-3" />
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-mt-1 sm:tw-mt-2.5 md:tw-mt-3 tw-w-32 tw-mr-5" v-for="i in 2" :key="i" />
            </div>
            <base-skeleton-loader class="tw-h-4 md:tw-h-5 tw-mt-2 sm:tw-mt-3 md:tw-mt-2 tw-w-24 tw-rounded-full tw-leading-3 md:tw-leading-5 tw-mb-0.5 md:tw-mb-0" />
          </div>
        </div>
        <div class="tw-flex tw-items-center" v-else>
          <div class="tw-h-16 tw-w-16 md:tw-h-24 md:tw-w-24 tw-flex-shrink-0">
            <Avatar :user-id="userId" editable avatar-type="user" />
          </div>
          <div class="tw-ml-4">
            <h1 class="tw-text-xl sm:tw-text-2xl md:tw-text-3xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">{{ user.fullName }}</h1>
            <div class="tw-text-gray-500 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-flex-wrap">
              <div class="tw-flex tw-items-center tw-mr-5">
                <mail-icon class="tw-h-3.5 tw-w-3.5 tw-mr-1.5 tw-flex-shrink-0" />
                <a class="hover:tw-text-primary tw-text-gray-500" :href="`mailto:${user.email}`">{{ user.email }}</a>
              </div>
              <div class="tw-flex tw-items-center">
                <phone-icon class="tw-h-3.5 tw-w-3.5 tw-mr-1.5 tw-flex-shrink-0" />
                <base-phone-number-field :isHyperlinkActive="false" :value="user.mobile" />
              </div>
            </div>
            <div class="tw-flex tw-flex-wrap">
              <base-badge class="tw-flex-shrink-0 tw-mt-1.5 tw-mr-1.5 sm:tw-mr-4 tw-leading-3 md:tw-leading-5" size="sm" v-if="hasPredictile">Predictile User</base-badge>
              <base-badge class="tw-flex-shrink-0 tw-mt-1.5 tw-mr-1.5 sm:tw-mr-4 tw-leading-3 md:tw-leading-5" size="sm" v-if="hasBusiness">Business User</base-badge>
              <base-badge class="tw-flex-shrink-0 tw-mt-1.5 tw-mr-1.5 sm:tw-mr-4 tw-leading-3 md:tw-leading-5" size="sm" v-if="hasPlans">Savings Member</base-badge>
            </div>
          </div>
        </div>
        <base-tabs class="tw-mt-2 tw-pb-4 md:tw-pb-0 tw-mb-0" no-border @click="tabClick($event)">
          <base-tab title="Personal Info" :selected="$route.meta.component === 'UserProfileUpdatePersonalInfo'" />
          <base-tab title="Member Payment Methods" :selected="$route.meta.component === 'UserProfileUpdateMemberPaymentMethods'" />
          <base-tab title="Change Password" :selected="$route.meta.component === 'UserProfileUpdateChangePassword'" />
          <base-tab title="Communication Settings" :selected="$route.meta.component === 'UserProfileUpdateCommunicationSettings'" />
        </base-tabs>
      </base-content>
    </div>
    <base-content>
      <keep-alive>
        <component :is="currentComponent" :user-id="userId" :user="user" :is-loading="isUserLoading" @user-updated="user = $event" />
      </keep-alive>
    </base-content>
  </div>
</template>

<script>
import Avatar from "@/components/Avatar";
import { MailIcon, PhoneIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";

export default {
  components: {
    Avatar,
    MailIcon,
    PhoneIcon,
    UserProfileUpdatePersonalInfo: () => import("@/views/user/components/UserProfileUpdatePersonalInfo"),
    UserProfileUpdateMemberPaymentMethods: () => import("@/views/user/components/UserProfileUpdateMemberPaymentMethods"),
    UserProfileUpdateChangePassword: () => import("@/views/user/components/UserProfileUpdateChangePassword"),
    UserProfileUpdateCommunicationSettings: () => import("@/views/user/components/UserProfileUpdateCommunicationSettings"),
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isUserLoading: false,
      user: {},
      plans: [],
      tabs: [
        { title: "Personal Info", component: "UserProfileUpdatePersonalInfo", route: "" },
        { title: "Member Payment Methods", component: "UserProfileUpdateMemberPaymentMethods", route: "payment-methods" },
        { title: "Change Password", component: "UserProfileUpdateChangePassword", route: "change-password" },
        { title: "Communication Settings", component: "UserProfileUpdateCommunicationSettings", route: "communication-settings" },
      ],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "organizations", "isOrganizationPredictile", "isOrganizationBusiness", "isOrganizationUser", "hasMultipleOrganizations"]),
    currentComponent() {
      return this.$route.meta.component;
    },
    hasPredictile() {
      return !!this.organizations.find((o) => o.type === "predictile");
    },
    hasBusiness() {
      return !!this.organizations.find((o) => o.type === "business");
    },
    hasPlans() {
      return !!this.plans.length;
    },
  },
  created() {
    this.getUser();
    this.getPlans();
  },
  methods: {
    getUser() {
      this.isUserLoading = true;

      this.$axios
        .get(`/users/${this.userId}`)
        .then((res) => {
          this.user = res.data;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error loading your profile information",
            message: "We had an issue loading your user profile. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.isUserLoading = false;
        });
    },
    getPlans() {
      this.$axios
        .get(`/users/${this.userId}/plans`)
        .then((res) => {
          this.plans = res.data;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error retrieving your plan details",
            message: "We had an issue retrieving your plans. Our technicians have been notified. You may want to try again.",
            error: { message: err.response.data },
          });
        })
        .finally(() => {
          this.isUserLoading = false;
        });
    },
    href(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    tabClick(tabTitle) {
      const tab = this.tabs.find((t) => t.title === tabTitle);
      this.href(`/users/${this.userId}/profile/${tab.route}`);
    },
  },
};
</script>
